import { Outlet, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { MyTrainingMaterialsDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import {
  MaterialItem,
  MaterialsList,
  getSortableTitle,
} from 'modules/trainings/components/MaterialsList'

export default function MyTrainingsMaterialsPage() {
  const { navigation } = useNavigation()
  const { trainingId } = useParams<{ trainingId: string }>()
  const source = useGraphqlDataCollectionSource({
    document: MyTrainingMaterialsDocument,
    search: {
      enabled: true,
    },
    mapData: (data): MaterialItem[] => {
      const trainingFiles = data.trainings.trainingFilesConnection.nodes.map((file) => ({
        id: file.id,
        title: file.title ?? file.filename,
        contentType: file.contentType,
        filename: file.filename,
        size: file.size,
        type: 'training' as const,
        url: file.url,
      }))

      const trainingLinks = data.trainings.trainingLinksConnection.nodes.map((link) => ({
        id: link.id,
        title: link.title,
        url: link.url,
        type: 'link' as const,
      }))

      const trainingClassFiles = data.trainings.trainingClassesConnection.nodes.flatMap(
        (trainingClass) =>
          trainingClass.classFilesConnection.nodes.map((file) => ({
            id: file.id,
            title: file.title ?? file.filename,
            contentType: file.contentType,
            filename: file.filename,
            size: file.size,
            type: 'trainingClass' as const,
          }))
      )

      const trainingClassLinks = data.trainings.trainingClassesConnection.nodes.flatMap(
        (trainingClass) =>
          trainingClass.classLinksConnection.nodes.map((link) => ({
            id: link.id,
            title: link.title,
            url: link.url,
            type: 'link' as const,
          }))
      )

      return [
        ...trainingFiles,
        ...trainingLinks,
        ...trainingClassFiles,
        ...trainingClassLinks,
      ].sort((a, b) => (getSortableTitle(a) > getSortableTitle(b) ? 1 : -1))
    },
    mapVariables: ({ search }) => ({
      trainingId: Number(trainingId),
      search: search,
    }),
    mapPagination: (data) => {
      const trainingFiles = data.trainings.trainingFilesConnection.totalCount ?? 0
      const trainingLinks = data.trainings.trainingLinksConnection.totalCount ?? 0
      const trainingClassFiles = data.trainings.trainingClassesConnection.nodes.reduce(
        (total, trainingClass) => total + (trainingClass.classFilesConnection.totalCount ?? 0),
        0
      )
      const trainingClassLinks = data.trainings.trainingClassesConnection.nodes.reduce(
        (total, trainingClass) => total + (trainingClass.classLinksConnection.totalCount ?? 0),
        0
      )
      return {
        total: trainingFiles + trainingLinks + trainingClassFiles + trainingClassLinks,
      }
    },
    itemUrl: (item) => {
      switch (item.type) {
        case 'link': {
          return item.url
        }
        default: {
          return navigation.myTrainings.revamp.detailsRevamp.view.materials.file.fullPath({
            trainingId: Number(trainingId),
            fileId: item.id,
            fileType: item.type,
          })
        }
      }
    },
  })

  return (
    <Box gap="s16">
      <SectionHeader
        title={i18n.t(
          'trainings.my_trainings_revamp.training_detail.materials.section_header.title'
        )}
        description={i18n.t(
          'trainings.my_trainings_revamp.training_detail.materials.section_header.description'
        )}
        separator="bottom"
      />
      <MaterialsList
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t(
              'trainings.my_trainings_revamp.training_detail.materials.datacollection.empty_state.no_data.title'
            ),
            description: i18n.t(
              'trainings.my_trainings_revamp.training_detail.materials.datacollection.empty_state.no_data.description'
            ),
          },
        }}
      />
      <Outlet />
    </Box>
  )
}
